'use client';

import { useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { useInView } from 'framer-motion';
import FCard from '@/components/FCard';
import ServiceModal from '@/components/ServiceModal';
import MagneticButton from '@/components/MagneticButton';
import type { ServiceKey } from '@/data/servicesData';

function ContactFCard() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.15, margin: '0px 0px -50px 0px' });

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    el.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  };

  return (
    <div
      ref={ref}
      id="card-5"
      className={`f-card reveal last-item-right${isInView ? ' active' : ''}`}
      style={{ '--i': 5 } as CSSProperties}
      onMouseMove={handleMouseMove}
    >
      <MagneticButton as="link" href="/contacto" className="contact-card-link">
        <div className="contact-card-icon">
          <svg className="card-icon-svg" viewBox="0 0 64 64">
            <rect x="6" y="14" width="52" height="36" rx="4" />
            <polyline points="6 14 32 36 58 14" />
          </svg>
        </div>
        <div className="contact-card-info">
          <span>Alianza</span>
          <h3>Contacto</h3>
          <p>¿Listo para empezar? Ponete en contacto hoy para diseñar una solución a medida.</p>
          <div className="v-btn-card magnetic-btn">Contactar →</div>
        </div>
      </MagneticButton>
    </div>
  );
}

const CARDS: Array<{
  id: string;
  key: ServiceKey;
  cardType: 'process' | 'courses';
  icon: React.ReactNode;
  tag: string;
  title: string;
  description: string;
  ctaLabel: string;
}> = [
  {
    id: 'card-0',
    key: 'busqueda',
    cardType: 'process',
    tag: 'Reclutamiento',
    title: 'Búsqueda y Selección',
    description:
      'Reclutamiento estratégico de perfiles profesionales y operativos mediante metodologías transparentes, modernas y eficientes.',
    ctaLabel: 'Ver Proceso →',
    icon: (
      <svg className="card-icon-svg" viewBox="0 0 64 64">
        <circle cx={28} cy={28} r={16} />
        <line x1={39} y1={39} x2={54} y2={54} />
        <path d="M28 20v16l10-6" />
      </svg>
    ),
  },
  {
    id: 'card-1',
    key: 'diagnostico',
    cardType: 'process',
    tag: 'Consultoría',
    title: 'Diagnóstico de Necesidades',
    description:
      'Realizamos un análisis profundo de tu organización para identificar brechas, oportunidades y diseñar un plan de acción personalizado orientado a resultados concretos.',
    ctaLabel: 'Ver Fases →',
    icon: (
      <svg className="card-icon-svg" viewBox="0 0 64 64">
        <rect x={10} y={8} width={44} height={48} rx={4} />
        <line x1={20} y1={20} x2={44} y2={20} />
        <line x1={20} y1={30} x2={44} y2={30} />
        <line x1={20} y1={40} x2={34} y2={40} />
        <polyline points="38 38 42 44 50 34" />
      </svg>
    ),
  },
  {
    id: 'card-2',
    key: 'lideres',
    cardType: 'courses',
    tag: 'Crecimiento',
    title: 'Formación de Líderes',
    description:
      'Programas de capacitación de alto impacto diseñados para potenciar habilidades de liderazgo, toma de decisiones y gestión de equipos en todos los niveles.',
    ctaLabel: 'Ver Cursos →',
    icon: (
      <svg className="card-icon-svg" viewBox="0 0 64 64">
        <path d="M32 6l6 18h18l-14 11 5 18-15-11-15 11 5-18L8 24h18z" />
      </svg>
    ),
  },
  {
    id: 'card-3',
    key: 'equipos',
    cardType: 'courses',
    tag: 'Sinergia',
    title: 'Cursos para Equipos',
    description:
      'Fortalecé la cohesión, comunicación y productividad de tus equipos con programas de formación enfocados en trabajo colaborativo y cultura organizacional.',
    ctaLabel: 'Ver Cursos →',
    icon: (
      <svg className="card-icon-svg" viewBox="0 0 64 64">
        <circle cx={32} cy={18} r={8} />
        <circle cx={14} cy={28} r={6} />
        <circle cx={50} cy={28} r={6} />
        <path d="M20 50c0-7 5-12 12-12s12 5 12 12" />
        <path d="M6 48c0-5 3-9 8-9" />
        <path d="M58 48c0-5-3-9-8-9" />
      </svg>
    ),
  },
  {
    id: 'card-4',
    key: 'rrhh',
    cardType: 'courses',
    tag: 'Gestión',
    title: 'Cursos para RRHH',
    description:
      'Formación técnica avanzada para profesionales de recursos humanos: gestión estratégica, selección por competencias, capacitación y experiencias significativas y habilidades blandas.',
    ctaLabel: 'Ver Cursos →',
    icon: (
      <svg className="card-icon-svg" viewBox="0 0 64 64">
        <rect x={8} y={14} width={48} height={36} rx={4} />
        <line x1={8} y1={24} x2={56} y2={24} />
        <line x1={22} y1={24} x2={22} y2={50} />
        <circle cx={14} cy={19} r={2} fill="currentColor" />
        <circle cx={20} cy={19} r={2} fill="currentColor" />
        <line x1={30} y1={33} x2={48} y2={33} />
        <line x1={30} y1={41} x2={42} y2={41} />
      </svg>
    ),
  },
];

export default function ServiciosInteractive() {
  const [activeCardId, setActiveCardId] = useState<ServiceKey | null>(null);

  // Hash deep-linking (e.g. /servicios#card-2): scroll the card into view,
  // same 2200ms delay as the original (waits out the header reveal).
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#card-')) {
      const timer = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 2200);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <>
      <div className="services-floating-grid stagger-children">
        {CARDS.map((card, i) => (
          <FCard
            key={card.id}
            id={card.id}
            index={i}
            cardType={card.cardType}
            icon={card.icon}
            tag={card.tag}
            title={card.title}
            description={card.description}
            ctaLabel={card.ctaLabel}
            onOpen={() => setActiveCardId(card.key)}
          />
        ))}
        <ContactFCard />
      </div>

      <ServiceModal activeCardId={activeCardId} onClose={() => setActiveCardId(null)} />
    </>
  );
}
